// Full game history (plan §6, ask #4): the member's games, most recent first,
// filterable by time horizon (This week / This month / All) and paged with a
// "Load more" button. Rows always open the game board; finished games stay in
// passive review until the member explicitly chooses Analyze.

import { api, type GameListItem } from "../api.ts";
import { esc, h } from "../dom.ts";
import { t, localeTag } from "../i18n/index.ts";
import { navigate } from "../router.ts";
import { currentUser } from "../session.ts";
import { topNav } from "../ui/topnav.ts";
import { pagedList } from "../ui/pagedList.ts";
import { buildPgn, metaFromServerGame, pgnFilename } from "../game/pgn.ts";
import { buildReview } from "../game/review.ts";
import { lookupOpening } from "../game/openings.ts";
import { downloadText } from "../ui/clipboard.ts";

type Horizon = "week" | "month" | "all";

export async function historyScreen(): Promise<HTMLElement> {
  const user = currentUser();
  if (!user) {
    navigate("/login");
    return h(`<main class="card"><p class="msg">${t("common.redirecting")}</p></main>`);
  }

  const el = h(`
    <main class="card wide">
      <section class="panel">
        <div class="panel-head">
          <h2>${t("history.title")}</h2>
          <button class="btn ghost export-all" type="button">${t("history.exportAll")}</button>
        </div>
        <p class="export-msg msg" role="status" hidden></p>
        <div class="segmented" role="tablist" aria-label="${t("history.timeHorizon")}">
          <button class="seg" role="tab" data-h="week">${t("history.thisWeek")}</button>
          <button class="seg" role="tab" data-h="month">${t("history.thisMonth")}</button>
          <button class="seg active" role="tab" data-h="all">${t("history.all")}</button>
        </div>
        <div class="history-slot"></div>
      </section>
    </main>
  `);
  el.prepend(topNav("games"));

  const seg = el.querySelector<HTMLElement>(".segmented")!;

  let horizon: Horizon = "all";

  const sinceFor = (hz: Horizon): number | undefined => {
    const now = Math.floor(Date.now() / 1000);
    if (hz === "week") return now - 7 * 86400;
    if (hz === "month") return now - 30 * 86400;
    return undefined;
  };

  // The shared paged-list helper (plan A5): the fetch closure reads the
  // currently-selected horizon, so a tab switch is just a reload().
  const paged = pagedList<GameListItem>({
    rowsContainer: h(`<div class="games history"></div>`),
    fetchPage: (offset, limit) =>
      api.listGames(limit, offset, sinceFor(horizon)).then((r) => ({ items: r.games, has_more: r.has_more })),
    renderRow: row,
    emptyText: () => (horizon === "all" ? t("history.emptyAll") : t("history.emptyRange")),
    errorText: t("history.loadFailed"),
  });
  el.querySelector<HTMLElement>(".history-slot")!.append(paged.element);

  seg.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>(".seg");
    const hz = btn?.dataset.h as Horizon | undefined;
    if (!hz || hz === horizon) return;
    horizon = hz;
    seg.querySelectorAll<HTMLElement>(".seg").forEach((b) => b.classList.toggle("active", b === btn));
    void paged.reload();
  });

  // "Export all" → the bulk server endpoint (A4), scoped to the current horizon.
  // Fetched (rather than a bare anchor) so we can read the `X-Export-Truncated`
  // header the server sets when the result hit its cap, and warn the member that
  // they didn't actually get *all* their games.
  const exportBtn = el.querySelector<HTMLButtonElement>(".export-all")!;
  const exportMsg = el.querySelector<HTMLElement>(".export-msg")!;
  const setExportMsg = (text: string, kind: "ok" | "bad" | "" = "") => {
    exportMsg.hidden = !text;
    exportMsg.textContent = text;
    exportMsg.className = "export-msg msg" + (kind ? ` ${kind}` : "");
  };
  exportBtn.addEventListener("click", async () => {
    if (exportBtn.disabled) return;
    exportBtn.disabled = true;
    const orig = exportBtn.textContent;
    exportBtn.textContent = t("history.exporting");
    setExportMsg("");
    try {
      const since = sinceFor(horizon);
      const url = `/api/games/export.pgn${since != null ? `?since=${since}` : ""}`;
      const res = await fetch(url, { credentials: "same-origin" });
      if (!res.ok) throw new Error(`export failed (${res.status})`);
      const text = await res.text();
      downloadText(filenameFromResponse(res), text, "application/x-chess-pgn");
      const cap = res.headers.get("X-Export-Truncated");
      if (cap) {
        setExportMsg(t("history.exportCapped", { cap }), "bad");
      }
    } catch {
      setExportMsg(t("history.exportFailed"), "bad");
    } finally {
      exportBtn.disabled = false;
      exportBtn.textContent = orig;
    }
  });

  // A ⬇ on a finished row downloads that single game's PGN. The click must be
  // caught here (before the router's document-level `data-link` handler) and its
  // default prevented so the row doesn't also navigate to the game board.
  paged.rows.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>("button[data-dl]");
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    void downloadGame(btn);
  });

  return el;
}

/** Fetch a single game and download it as a `.pgn` (⬇ affordance on rows). */
async function downloadGame(btn: HTMLButtonElement): Promise<void> {
  const id = btn.dataset.dl!;
  if (btn.disabled) return;
  btn.disabled = true;
  const orig = btn.textContent;
  btn.textContent = "…";
  try {
    const full = await api.getGame(id);
    const review = buildReview(full.moves);
    const meta = metaFromServerGame(full);
    const hit = await lookupOpening(review);
    if (hit) {
      meta.eco = hit.eco;
      meta.opening = hit.name;
    }
    downloadText(pgnFilename(meta), buildPgn(review, meta));
    btn.textContent = "✓";
    window.setTimeout(() => (btn.textContent = orig), 1200);
  } catch {
    btn.textContent = "✗";
    window.setTimeout(() => (btn.textContent = orig), 1200);
  } finally {
    btn.disabled = false;
  }
}

function row(g: GameListItem): HTMLElement {
  const finished = g.status !== "started";
  const a = document.createElement("a");
  a.className = "game-row";
  a.setAttribute("data-link", "");
  // Opening a finished game must not start Stockfish. The regular game board is
  // already a read-only review surface with first/previous/next/last controls
  // and an explicit Analyze action in its result card.
  a.href = `/game/${g.id}`;
  const badge = finished ? g.result : "playing";
  const diff =
    g.rating_diff == null
      ? ""
      : `<span class="rdiff ${g.rating_diff > 0 ? "up" : g.rating_diff < 0 ? "down" : ""}">${
          g.rating_diff > 0 ? "+" : ""
        }${g.rating_diff}</span>`;
  a.innerHTML = `
    <span class="opp">${t("home.vs", { who: esc(g.opponent) })}</span>
    <span class="meta">${esc(g.tc)}${g.rated ? ` · ${t("chess.rated")}` : ""} · ${t("chess." + g.color)}${
      g.finished_at ? ` · ${fmtDate(g.finished_at)}` : ""
    }</span>
    ${diff}
    <span class="badge ${badge}">${g.lifecycle === "paused" ? t("home.paused") : t("result." + badge)}</span>
    ${finished ? `<button class="row-dl" type="button" data-dl="${esc(g.id)}" title="${t("history.downloadPgn")}" aria-label="${t("history.downloadPgn")}">⬇</button>` : ""}`;
  return a;
}

/** Unix seconds → a compact local date like "6 Jul". */
function fmtDate(unix: number): string {
  const d = new Date(unix * 1000);
  return d.toLocaleDateString(localeTag(), { day: "numeric", month: "short" });
}

/** The download filename from a response's Content-Disposition, or a default. */
function filenameFromResponse(res: Response): string {
  const cd = res.headers.get("Content-Disposition") ?? "";
  const m = /filename="?([^"]+)"?/.exec(cd);
  return m?.[1] || "chess-games.pgn";
}
